import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, ArrowLeftRight, Zap, AlertTriangle, CheckCircle2, Loader2, ArrowDown, ArrowUp, Settings2, Webhook, Play, Plug, Map, History, Clock, XCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import api from '../../lib/api';

const TABS = [
  { id: 'config', label: 'Configuration', icon: Settings2 },
  { id: 'history', label: 'Sync History', icon: History },
  { id: 'events', label: 'Events', icon: ArrowLeftRight },
  { id: 'conflicts', label: 'Conflicts', icon: AlertTriangle },
  { id: 'mappings', label: 'Field Mapping', icon: Map },
  { id: 'stream', label: 'Event Stream', icon: Webhook },
];

const LOCKENE_FIELDS = ['name', 'email', 'phone', 'company', 'value', 'notes', 'city', 'state', 'country', 'source', 'status'];

export default function CRMBidirectionalSync() {
  const [activeTab, setActiveTab] = useState('config');

  return (
    <div className="space-y-6" data-testid="crm-sync-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">CRM Bi-Directional Sync</h1>
        <p className="text-sm text-muted-foreground mt-1">Real-time sync between Lockene and external CRMs with conflict resolution & event streaming</p>
      </div>

      <div className="flex gap-1 p-1 bg-muted/50 rounded-lg w-fit flex-wrap" data-testid="sync-tabs">
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors ${activeTab === tab.id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid={`sync-tab-${tab.id}`}
          ><tab.icon className="h-3.5 w-3.5" />{tab.label}</button>
        ))}
      </div>

      {activeTab === 'config' && <SyncConfigPanel />}
      {activeTab === 'history' && <SyncHistory />}
      {activeTab === 'events' && <SyncEvents />}
      {activeTab === 'conflicts' && <ConflictsPanel />}
      {activeTab === 'mappings' && <FieldMappingPanel />}
      {activeTab === 'stream' && <EventStreamPanel />}
    </div>
  );
}

/* ── Configuration ────────────────────────────────────────────────── */

function SyncConfigPanel() {
  const [config, setConfig] = useState({ crm_type: 'hubspot', direction: 'bidirectional', sync_entities: ['leads', 'deals', 'contacts'], conflict_resolution: 'lockene_wins', auto_sync_interval: 300, enabled: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const [summary, setSummary] = useState(null);

  useEffect(() => { loadConfig(); loadSummary(); }, []);

  const loadConfig = async () => {
    try {
      const res = await api.get('/api/sync/config');
      if (res.data?.config) setConfig(prev => ({ ...prev, ...res.data.config }));
      setWebhookUrl(res.data?.webhook_url || '');
    } catch {} finally { setLoading(false); }
  };

  const loadSummary = async () => {
    try { const res = await api.get('/api/sync/events/summary'); setSummary(res.data); } catch {}
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const res = await api.put('/api/sync/config', config);
      if (res.data?.webhook_url) setWebhookUrl(res.data.webhook_url);
      toast.success('Sync configuration saved');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const testConnection = async () => {
    setTesting(true); setTestResult(null);
    try {
      const res = await api.post('/api/sync/test-connection');
      setTestResult(res.data);
      if (res.data?.success) toast.success('Connection successful');
      else toast.error(res.data?.message || 'Connection failed');
    } catch (err) { setTestResult({ success: false, message: err.response?.data?.detail || 'Test failed' }); toast.error('Connection test failed'); }
    finally { setTesting(false); }
  };

  const syncNow = async () => {
    setSyncing(true); setSyncResult(null);
    try {
      const res = await api.post('/api/sync/pull?limit=50');
      setSyncResult(res.data);
      toast.success(res.data?.message || 'Sync completed');
      loadSummary();
    } catch (err) { toast.error(err.response?.data?.detail || 'Sync failed'); setSyncResult({ success: false, message: err.response?.data?.detail }); }
    finally { setSyncing(false); }
  };

  if (loading) return <div className="p-8 text-muted-foreground flex items-center gap-2"><Loader2 className="h-5 w-5 animate-spin" />Loading...</div>;

  return (
    <div className="space-y-6" data-testid="sync-config">
      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <SummaryCard label="Total Events" value={summary.total_events} />
          <SummaryCard label="Inbound" value={summary.inbound} icon={<ArrowDown className="h-3 w-3 text-blue-400" />} />
          <SummaryCard label="Outbound" value={summary.outbound} icon={<ArrowUp className="h-3 w-3 text-violet-400" />} />
          <SummaryCard label="Conflicts" value={summary.conflicts} className={summary.conflicts > 0 ? 'text-amber-400' : ''} />
          <SummaryCard label="Health" value={summary.health} className={summary.health === 'good' ? 'text-emerald-400' : summary.health === 'warning' ? 'text-amber-400' : 'text-red-400'} />
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="text-lg">Sync Configuration</CardTitle>
              <CardDescription>Configure bi-directional sync with your external CRM</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={testConnection} disabled={testing} data-testid="sync-test-btn">
                {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Plug className="h-3.5 w-3.5 mr-1" />}
                Test Connection
              </Button>
              <Button size="sm" onClick={syncNow} disabled={syncing || !config.enabled} data-testid="sync-now-btn">
                {syncing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Play className="h-3.5 w-3.5 mr-1" />}
                Sync Now
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>CRM Provider</Label>
              <Select value={config.crm_type} onValueChange={v => setConfig(p => ({ ...p, crm_type: v }))}>
                <SelectTrigger data-testid="sync-crm-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hubspot">HubSpot</SelectItem>
                  <SelectItem value="salesforce">Salesforce</SelectItem>
                  <SelectItem value="zoho">Zoho CRM</SelectItem>
                  <SelectItem value="freshworks">Freshworks</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Sync Direction</Label>
              <Select value={config.direction} onValueChange={v => setConfig(p => ({ ...p, direction: v }))}>
                <SelectTrigger data-testid="sync-direction-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bidirectional">Bi-Directional</SelectItem>
                  <SelectItem value="inbound">Inbound Only (External to Lockene)</SelectItem>
                  <SelectItem value="outbound">Outbound Only (Lockene to External)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Conflict Resolution</Label>
              <Select value={config.conflict_resolution} onValueChange={v => setConfig(p => ({ ...p, conflict_resolution: v }))}>
                <SelectTrigger data-testid="sync-conflict-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="lockene_wins">Lockene Wins</SelectItem>
                  <SelectItem value="external_wins">External Wins</SelectItem>
                  <SelectItem value="newest_wins">Newest Wins</SelectItem>
                  <SelectItem value="manual">Manual Review</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Auto-Sync Interval (seconds)</Label>
              <Input type="number" data-testid="sync-interval" value={config.auto_sync_interval} onChange={e => setConfig(p => ({ ...p, auto_sync_interval: parseInt(e.target.value) || 300 }))} />
              <p className="text-xs text-muted-foreground mt-1">{Math.round(config.auto_sync_interval / 60)} minutes between auto-syncs</p>
            </div>
          </div>

          {webhookUrl && (
            <div>
              <Label>Webhook URL (for inbound events)</Label>
              <Input value={`${window.location.origin}${webhookUrl}`} readOnly className="font-mono text-xs bg-muted" data-testid="sync-webhook-url" />
              <p className="text-xs text-muted-foreground mt-1">Configure this URL in your external CRM's webhook settings</p>
            </div>
          )}

          <div className="flex items-center gap-2 border-t border-border/40 pt-4">
            <Switch data-testid="sync-enabled-toggle" checked={config.enabled} onCheckedChange={v => setConfig(p => ({ ...p, enabled: v }))} />
            <Label className="font-medium">Enable Sync</Label>
            <Badge variant="outline" className={`ml-2 text-[10px] ${config.enabled ? 'text-emerald-400 border-emerald-500/30' : 'text-muted-foreground'}`}>
              {config.enabled ? 'ACTIVE' : 'DISABLED'}
            </Badge>
          </div>

          <Button onClick={saveConfig} disabled={saving} data-testid="sync-save-btn" className="w-full">
            {saving ? 'Saving...' : 'Save Configuration'}
          </Button>

          {/* Connection Test Result */}
          {testResult && (
            <div className={`flex items-start gap-2 p-3 rounded-lg text-sm ${testResult.success ? 'bg-emerald-500/10 text-emerald-300' : 'bg-red-500/10 text-red-300'}`} data-testid="test-result">
              {testResult.success ? <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" /> : <XCircle className="h-4 w-4 mt-0.5 shrink-0" />}
              <div>
                <p className="font-medium">{testResult.success ? 'Connection Successful' : 'Connection Failed'}</p>
                <p className="text-xs opacity-80 mt-0.5">{testResult.message} {testResult.mode && `(${testResult.mode} mode)`}</p>
              </div>
            </div>
          )}

          {/* Sync Result */}
          {syncResult && (
            <div className={`flex items-start gap-2 p-3 rounded-lg text-sm ${syncResult.success ? 'bg-emerald-500/10 text-emerald-300' : 'bg-red-500/10 text-red-300'}`} data-testid="sync-result">
              {syncResult.success ? <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" /> : <XCircle className="h-4 w-4 mt-0.5 shrink-0" />}
              <div>
                <p className="font-medium">{syncResult.message || 'Sync completed'}</p>
                {syncResult.success && (
                  <p className="text-xs opacity-80 mt-0.5">
                    Pulled: {syncResult.pulled} | Created: {syncResult.created} | Updated: {syncResult.updated} | Errors: {syncResult.errors}
                  </p>
                )}
              </div>
            </div>
          )}

          <p className="text-xs text-muted-foreground">Currently in simulation mode. Production mode requires real API credentials from your CRM provider.</p>
        </CardContent>
      </Card>
    </div>
  );
}

/* ── Sync Run History ─────────────────────────────────────────────── */

function SyncHistory() {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadRuns(); }, []);

  const loadRuns = async () => {
    try { const res = await api.get('/api/sync/runs?limit=20'); setRuns(res.data?.runs || []); } catch {} finally { setLoading(false); }
  };

  const statusIcon = (s) => {
    if (s === 'completed') return <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />;
    if (s === 'running') return <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-400" />;
    if (s === 'failed') return <XCircle className="h-3.5 w-3.5 text-red-400" />;
    return <Clock className="h-3.5 w-3.5 text-muted-foreground" />;
  };

  return (
    <div className="space-y-6" data-testid="sync-history">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="text-lg">Sync Run History</CardTitle>
              <CardDescription>Track every sync operation between Lockene and external CRMs</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={loadRuns}><RefreshCw className="h-4 w-4 mr-1" />Refresh</Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? <div className="text-center p-4"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div> :
           runs.length === 0 ? (
             <div className="text-center py-8">
               <History className="h-10 w-10 mx-auto text-muted-foreground/30 mb-2" />
               <p className="text-sm text-muted-foreground">No sync runs yet. Click "Sync Now" on the Configuration tab to start.</p>
             </div>
           ) : (
            <div className="space-y-2">
              {runs.map(run => (
                <div key={run.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg" data-testid={`sync-run-${run.id}`}>
                  <div className="flex items-center gap-3">
                    {statusIcon(run.status)}
                    <div>
                      <span className="text-sm font-medium flex items-center gap-2">
                        {run.direction === 'pull' ? 'Pull from' : 'Push to'} {run.crm_type || 'CRM'}
                        <Badge variant="outline" className={`text-[10px] ${run.status === 'completed' ? 'text-emerald-400 border-emerald-500/30' : run.status === 'failed' ? 'text-red-400 border-red-500/30' : 'text-blue-400 border-blue-500/30'}`}>{run.status}</Badge>
                      </span>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {run.started_at ? new Date(run.started_at).toLocaleString() : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    {run.status === 'completed' && (
                      <>
                        <span>Pulled: <strong className="text-foreground">{run.pulled || 0}</strong></span>
                        <span>Created: <strong className="text-emerald-400">{run.created || 0}</strong></span>
                        <span>Updated: <strong className="text-blue-400">{run.updated || 0}</strong></span>
                        {(run.errors || 0) > 0 && <span>Errors: <strong className="text-red-400">{run.errors}</strong></span>}
                      </>
                    )}
                    {run.error && <span className="text-red-400 max-w-[200px] truncate">{run.error}</span>}
                  </div>
                </div>
              ))}
            </div>
           )}
        </CardContent>
      </Card>
    </div>
  );
}

/* ── Sync Events ──────────────────────────────────────────────────── */

function SyncEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => { loadEvents(); }, [filter]);

  const loadEvents = async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? `?direction=${filter}&limit=50` : '?limit=50';
      const res = await api.get(`/api/sync/events${params}`);
      setEvents(res.data?.events || []);
    } catch {} finally { setLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="sync-events">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-lg">Sync Events</CardTitle>
            <div className="flex gap-2">
              {['all', 'inbound', 'outbound', 'internal'].map(f => (
                <button key={f} onClick={() => setFilter(f)}
                  className={`px-2.5 py-1 text-xs rounded-md transition-colors ${filter === f ? 'bg-violet-500/15 text-violet-300' : 'text-muted-foreground hover:text-foreground'}`}
                  data-testid={`event-filter-${f}`}
                >{f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}</button>
              ))}
              <Button variant="outline" size="sm" onClick={loadEvents}><RefreshCw className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? <div className="text-center p-4"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div> :
           events.length === 0 ? <p className="text-sm text-muted-foreground text-center py-6">No events matching filter</p> :
           <div className="space-y-1.5 max-h-[500px] overflow-y-auto">{events.map(e => (
             <div key={e.id} className="flex items-center justify-between p-2.5 bg-muted/30 rounded-lg text-sm">
               <div className="flex items-center gap-2">
                 {e.direction === 'inbound' ? <ArrowDown className="h-3.5 w-3.5 text-blue-400" /> :
                  e.direction === 'outbound' ? <ArrowUp className="h-3.5 w-3.5 text-violet-400" /> :
                  <Zap className="h-3.5 w-3.5 text-amber-400" />}
                 <div>
                   <p className="font-medium">{e.event_type?.replace(/_/g, ' ')}</p>
                   <p className="text-xs text-muted-foreground">{e.entity_type} {e.entity_id ? `· ${e.entity_id.slice(0, 8)}...` : ''} {e.source ? `· ${e.source}` : ''}</p>
                 </div>
               </div>
               <div className="flex items-center gap-2">
                 <Badge variant="outline" className={`text-[10px] ${
                   e.status === 'processed' || e.status === 'simulated' || e.status === 'emitted' ? 'text-emerald-400 border-emerald-500/30' :
                   e.status === 'conflict' ? 'text-amber-400 border-amber-500/30' :
                   'text-blue-400 border-blue-500/30'}`}>{e.status}</Badge>
                 <span className="text-xs text-muted-foreground w-[70px] text-right">{e.created_at ? new Date(e.created_at).toLocaleTimeString() : ''}</span>
               </div>
             </div>
           ))}</div>}
        </CardContent>
      </Card>
    </div>
  );
}

/* ── Conflicts ────────────────────────────────────────────────────── */

function ConflictsPanel() {
  const [conflicts, setConflicts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadConflicts(); }, []);

  const loadConflicts = async () => {
    try { const res = await api.get('/api/sync/conflicts'); setConflicts(res.data?.conflicts || []); } catch {} finally { setLoading(false); }
  };

  const resolveConflict = async (eventId, resolution) => {
    try {
      await api.post(`/api/sync/conflicts/${eventId}/resolve?resolution=${resolution}`);
      toast.success('Conflict resolved');
      loadConflicts();
    } catch { toast.error('Failed to resolve'); }
  };

  return (
    <div className="space-y-6" data-testid="sync-conflicts">
      <Card>
        <CardHeader><CardTitle className="text-lg">Sync Conflicts</CardTitle><CardDescription>Review and resolve data conflicts between Lockene and external CRM</CardDescription></CardHeader>
        <CardContent>
          {loading ? <div className="text-center p-4"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div> :
           conflicts.length === 0 ? (
             <div className="text-center py-8"><CheckCircle2 className="h-10 w-10 mx-auto text-emerald-500/30 mb-2" /><p className="text-sm text-muted-foreground">No unresolved conflicts</p></div>
           ) : (
             <div className="space-y-3">{conflicts.map(c => (
               <Card key={c.id} className="border-amber-500/20">
                 <CardContent className="p-4">
                   <div className="flex justify-between items-start">
                     <div>
                       <p className="font-medium text-sm">{c.entity_type} conflict</p>
                       <p className="text-xs text-muted-foreground mt-1">Entity: {c.entity_id?.slice(0, 12)}... | Source: {c.source}</p>
                       {c.conflict_details && <pre className="text-xs bg-muted p-2 rounded mt-2 max-h-24 overflow-auto">{JSON.stringify(c.conflict_details, null, 2)}</pre>}
                     </div>
                     <div className="flex gap-1.5">
                       <Button size="sm" variant="outline" onClick={() => resolveConflict(c.id, 'lockene_wins')} data-testid={`resolve-local-${c.id}`}>Keep Local</Button>
                       <Button size="sm" variant="outline" onClick={() => resolveConflict(c.id, 'external_wins')} data-testid={`resolve-ext-${c.id}`}>Accept External</Button>
                     </div>
                   </div>
                 </CardContent>
               </Card>
             ))}</div>
           )}
        </CardContent>
      </Card>
    </div>
  );
}

/* ── Field Mapping ────────────────────────────────────────────────── */

function FieldMappingPanel() {
  const [mappings, setMappings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadMappings(); }, []);

  const loadMappings = async () => {
    try {
      const res = await api.get('/api/sync/field-mappings');
      setMappings(res.data?.mappings || {});
    } catch {} finally { setLoading(false); }
  };

  const saveMappings = async () => {
    setSaving(true);
    try {
      await api.put('/api/sync/field-mappings', { mappings, crm_type: 'hubspot' });
      toast.success('Field mappings saved');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const updateMapping = (extField, lockeneField) => {
    setMappings(prev => ({ ...prev, [extField]: lockeneField }));
  };

  const addMapping = () => {
    setMappings(prev => ({ ...prev, [`custom_field_${Object.keys(prev).length + 1}`]: 'notes' }));
  };

  const removeMapping = (extField) => {
    setMappings(prev => {
      const next = { ...prev };
      delete next[extField];
      return next;
    });
  };

  if (loading) return <div className="p-8 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="field-mappings">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="text-lg">Field Mapping</CardTitle>
              <CardDescription>Map fields between your external CRM and Lockene's lead schema</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={addMapping} data-testid="add-mapping-btn">Add Field</Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-[1fr_auto_1fr_auto] gap-2 items-center text-xs font-medium text-muted-foreground pb-2 border-b border-border/40">
            <span>External CRM Field</span>
            <span></span>
            <span>Lockene Field</span>
            <span></span>
          </div>
          {Object.entries(mappings).map(([extField, lockeneField]) => (
            <div key={extField} className="grid grid-cols-[1fr_auto_1fr_auto] gap-2 items-center">
              <Input
                value={extField}
                onChange={e => {
                  const newKey = e.target.value;
                  setMappings(prev => {
                    const entries = Object.entries(prev);
                    const idx = entries.findIndex(([k]) => k === extField);
                    if (idx >= 0) entries[idx] = [newKey, lockeneField];
                    return Object.fromEntries(entries);
                  });
                }}
                className="text-sm h-8"
                data-testid={`ext-field-${extField}`}
              />
              <ArrowLeftRight className="h-3.5 w-3.5 text-muted-foreground" />
              <Select value={lockeneField} onValueChange={v => updateMapping(extField, v)}>
                <SelectTrigger className="h-8 text-sm" data-testid={`lockene-field-${extField}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LOCKENE_FIELDS.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:text-red-400" onClick={() => removeMapping(extField)} data-testid={`remove-mapping-${extField}`}>
                <XCircle className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
          <Button onClick={saveMappings} disabled={saving} data-testid="save-mappings-btn" className="w-full mt-4">
            {saving ? 'Saving...' : 'Save Mappings'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

/* ── Event Stream ─────────────────────────────────────────────────── */

function EventStreamPanel() {
  const [sub, setSub] = useState({ webhook_url: '', events: ['deal_created', 'deal_updated', 'lead_created', 'lead_updated'], enabled: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [streamEvents, setStreamEvents] = useState([]);
  const [loadingStream, setLoadingStream] = useState(false);

  useEffect(() => { loadSubscription(); }, []);

  const loadSubscription = async () => {
    try { const res = await api.get('/api/sync/event-stream/subscribe'); if (res.data?.configured) setSub(prev => ({ ...prev, ...res.data })); } catch {} finally { setLoading(false); }
  };

  const saveSubscription = async () => {
    setSaving(true);
    try {
      await api.put('/api/sync/event-stream/subscribe', sub);
      toast.success('Event stream subscription saved');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const fetchStream = async () => {
    setLoadingStream(true);
    try {
      const res = await api.get('/api/sync/event-stream?limit=30');
      setStreamEvents(res.data?.events || []);
    } catch {} finally { setLoadingStream(false); }
  };

  const ALL_EVENTS = ['deal_created', 'deal_updated', 'deal_stage_changed', 'lead_created', 'lead_updated', 'lead_status_changed', 'quote_created', 'invoice_created', 'invoice_paid', 'client_created', 'client_updated'];

  if (loading) return <div className="p-8 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="event-stream">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Event Stream for BI Tools</CardTitle>
          <CardDescription>Configure webhook subscriptions to stream CRM events to your BI/analytics platform</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Webhook URL</Label>
            <Input data-testid="stream-webhook-url" value={sub.webhook_url} onChange={e => setSub(p => ({ ...p, webhook_url: e.target.value }))} placeholder="https://your-bi-tool.com/webhook/lockene" />
            <p className="text-xs text-muted-foreground mt-1">Events will be POSTed to this URL in real-time when they occur</p>
          </div>
          <div>
            <Label className="text-sm mb-2 block">Event Types to Stream</Label>
            <div className="flex flex-wrap gap-2">
              {ALL_EVENTS.map(evt => (
                <button key={evt} onClick={() => setSub(p => ({ ...p, events: p.events.includes(evt) ? p.events.filter(e => e !== evt) : [...p.events, evt] }))}
                  className={`text-xs px-2.5 py-1.5 rounded-full border transition-colors ${sub.events.includes(evt) ? 'bg-violet-500/15 text-violet-300 border-violet-500/30' : 'border-border/60 text-muted-foreground hover:text-foreground'}`}
                  data-testid={`stream-event-${evt}`}
                >{evt.replace(/_/g, ' ')}</button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2 border-t border-border/40 pt-4">
            <Switch data-testid="stream-enabled-toggle" checked={sub.enabled} onCheckedChange={v => setSub(p => ({ ...p, enabled: v }))} />
            <Label>Enable Event Streaming</Label>
          </div>
          <Button onClick={saveSubscription} disabled={saving} data-testid="stream-save-btn" className="w-full">
            {saving ? 'Saving...' : 'Save Subscription'}
          </Button>

          {/* Polling endpoint info */}
          <div className="bg-muted/30 p-3 rounded-lg">
            <p className="text-xs font-medium mb-1">Polling API Endpoint</p>
            <code className="text-xs text-violet-400">GET /api/sync/event-stream?since=2026-01-01T00:00:00Z&limit=100</code>
            <p className="text-xs text-muted-foreground mt-1">Use this if your BI tool prefers polling over webhooks</p>
          </div>
        </CardContent>
      </Card>

      {/* Live Event Stream Preview */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-lg">Event Stream Preview</CardTitle>
            <Button variant="outline" size="sm" onClick={fetchStream} disabled={loadingStream} data-testid="fetch-stream-btn">
              {loadingStream ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
              Fetch Events
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {streamEvents.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">Click "Fetch Events" to preview recent events from the stream</p>
          ) : (
            <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
              {streamEvents.map((e, i) => (
                <div key={e.id || i} className="flex items-center justify-between p-2 bg-muted/30 rounded text-xs">
                  <div className="flex items-center gap-2">
                    <Zap className="h-3 w-3 text-amber-400" />
                    <span className="font-medium">{e.event_type?.replace(/_/g, ' ')}</span>
                    <span className="text-muted-foreground">{e.entity_type}</span>
                  </div>
                  <span className="text-muted-foreground">{e.created_at ? new Date(e.created_at).toLocaleTimeString() : ''}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* ── Helper Components ────────────────────────────────────────────── */

function SummaryCard({ label, value, icon, className = '' }) {
  return (
    <Card>
      <CardContent className="p-3 text-center">
        <p className={`text-xl font-bold flex items-center justify-center gap-1 ${className}`}>
          {icon}{value}
        </p>
        <p className="text-[10px] text-muted-foreground">{label}</p>
      </CardContent>
    </Card>
  );
}
